import React, { useState } from "react";
import type { Step, HubSpotProduct, CustomerSelections } from "../../types/configurator";
import { formatPrice } from "../../utils";

interface Props {
  steps: Step[];
  productMap: Record<string, HubSpotProduct>;
}

export const CustomerPreview: React.FC<Props> = ({ steps, productMap }) => {
  const [selections, setSelections] = useState<CustomerSelections>({});

  const getVisibleSteps = () =>
    steps.filter((s) => {
      const conditions = s.conditions ?? [];
      if (conditions.length === 0) return true;
      return conditions.every(({ stepId, selectedAny }) => {
        const condStep = steps.find((st) => st.id === stepId);
        if (!condStep) return true;
        if (selectedAny.length === 0) return true;
        const sel = selections[condStep.id] || [];
        return selectedAny.some((pid) => sel.includes(pid));
      });
    });

  const toggleSelection = (stepId: string, productId: string, type: string) => {
    setSelections((prev) => {
      const current = prev[stepId] || [];
      if (type === "single") {
        return { ...prev, [stepId]: current.includes(productId) ? [] : [productId] };
      }
      return {
        ...prev,
        [stepId]: current.includes(productId)
          ? current.filter((id) => id !== productId)
          : [...current, productId],
      };
    });
  };

  const visible = getVisibleSteps();

  return (
    <div className="customer-preview">
      {steps.map((step, idx) => {
        const isVisible = visible.includes(step);
        const sel = selections[step.id] || [];

        return (
          <div key={step.id}>
            <div className={`customer-step ${!isVisible ? "dimmed" : ""}`}>
              <h3>
                <span className="cs-num">{idx + 1}</span>
                {step.title || `Step ${idx + 1}`}
                {step.selectionType === "multi" && (
                  <span className="badge badge-multi" style={{ fontSize: 9 }}>
                    Select multiple
                  </span>
                )}
                {!isVisible && (
                  <span
                    className="badge"
                    style={{
                      background: "var(--bg-hover)",
                      color: "var(--text-muted)",
                      fontSize: 9,
                    }}
                  >
                    Hidden
                  </span>
                )}
              </h3>
              <div className="customer-options">
                {step.items.map((item) => {
                  if (item.type === 'product') {
                    const p = productMap[item.productId];
                    if (!p) return null;
                    return (
                      <button
                        key={item.uid}
                        className={`customer-option ${sel.includes(item.productId) ? "selected" : ""}`}
                        onClick={() =>
                          isVisible && toggleSelection(step.id, item.productId, step.selectionType)
                        }
                      >
                        {p.imageUrl && (
                          <img
                            src={p.imageUrl}
                            alt={p.name}
                            style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 4, display: 'block', marginBottom: 6 }}
                          />
                        )}
                        <span style={{ display: 'block', fontWeight: 500 }}>{p.name}</span>
                        <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 11 }}>{formatPrice(p.price)}</span>
                      </button>
                    );
                  } else {
                    return (
                      <button
                        key={item.uid}
                        className={`customer-option ${sel.includes(item.value) ? "selected" : ""}`}
                        onClick={() =>
                          isVisible && toggleSelection(step.id, item.value, step.selectionType)
                        }
                      >
                        {item.label}
                      </button>
                    );
                  }
                })}
                {step.items.length === 0 && (
                  <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
                    No products assigned
                  </span>
                )}
              </div>
            </div>
            {idx < steps.length - 1 && (
              <div className="step-connector">
                <svg width="2" height="16">
                  <line
                    x1="1" y1="0" x2="1" y2="16"
                    stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 3"
                  />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
